<script setup lang="ts">
import { computed, provide, reactive, ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import logoIcon from '@/assets/logo2.png'
import logoutIcon from '@/assets/logout.png'
import homemetricIcon from '@/assets/Homemetric.png'
import categoryIcon from '@/assets/category.png'
import usersIcon from '@/assets/users@2x.png'
import technicianIcon from '@/assets/Technician.png'
import scheduleIcon from '@/assets/schedule@2x.png'
import mapsIcon from '@/assets/maps@2x.png'
import couponIcon from '@/assets/coupon@2x.png'
import incomeIcon from '@/assets/income@2x.png'
import navArrowUpIcon from '@/assets/uparrow.png'
import navArrowDownIcon from '@/assets/downarrow.png'
import ProfilePage from './components/ProfilePage.vue'
import ChangePassword from './components/ChangePassword.vue'
import TechniciansPage from '@/views/technicianspage/components/TechniciansPage.vue'
import UsersPage from '@/views/userspage/components/UsersPage.vue'
import IncomeLogPage from './IncomeLogPage.vue'
import SettingsPage from './SettingsPage.vue'
import CouponCenter from '@/views/CouponsPage/CouponCenter.vue'
import CouponUsageLog from '@/views/CouponsPage/CouponUsageLog.vue'
import SchedulePage from '@/views/SchedulePage/SchedulePage.vue'
import TMPage from '@/views/TMPage/TMPage.vue'
import HMCareSubscriptionsPage from './HMCareSubscriptionsPage/index.vue'
import HMCareOrders from './components/HMCareOrders.vue'
import ToastComponent from '@/components/ToastComponent.vue'
import { TOAST_DURATION } from '@/constants/toast'
import ChecklistItems from '@/views/Librarypage/components/ChecklistItems.vue'
import CareTemplates from '@/views/Librarypage/components/CareTemplates.vue'
import DefaultChecklist from '@/views/Librarypage/DefaultChecklist.vue'
import type { OrderRecord } from '@/api/hmCareOrderTypes'
import { formatCareSidebarBadge } from '@/api/hmCareOrderTypes'
import PremiumCare from '@/views/Librarypage/components/PremiumCare.vue'
import EssentialCareTemplates from '@/views/Librarypage/components/EssentialCare.vue'
import EnhancedCareTemplates from '@/views/Librarypage/components/EnhancedCare.vue'
import { refreshCareSidebarBadges, type CareSidebarBadgeRefreshResult } from '@/utils/careSidebarBadgeSync'
import {
  type CareSubKey,
  careSubKeyFromRoute,
  careOrdersListRouteForSub,
  careOrdersDetailRoute,
  isCareOrdersOverlayListRoute,
} from '@/utils/careOrdersRoutes'
import { peekHmCareOrdersListPrefill } from '@/api/CareOrderDetailFocus'
import {
  type CareTemplateTab,
  careTemplateTabFromRoute,
  careTemplatesRoute,
  libraryListRouteForSub,
  librarySubKeyFromRoute,
  techniciansRoute,
  usersRoute,
  isUsersRoute,
  incomeLogRoute,
  settingsRoute,
  scheduleRoute,
  tasksMapRoute,
  couponRouteForSub,
  couponSubKeyFromRoute,
  type CouponSubKey,
} from '@/utils/libraryRoutes'
import { getAdminProfile, type AdminProfile } from '@/api/admin'

type CareSubKeyNav = CareSubKey

const router = useRouter()
const route = useRoute()

const activePanel = reactive({
  panel: null as 'profile' | 'password' | 'technicians' | null,
})

const sidebarAvatar = ref('')
const sidebarName = ref('')
const sidebarRole = ref('')
const currentPage = ref('home')
const showTechniciansPage = ref(false)
const careExpanded = ref(true)
const careActiveSub = ref<CareSubKeyNav>('hm_orders')
const libraryExpanded = ref(false)
const libraryActiveSub = ref<'checklist' | 'templates' | 'default-checklist'>('checklist')
const couponsExpanded = ref(false)
const couponActiveSub = ref<CouponSubKey>('center')
const careTemplateTab = ref<CareTemplateTab>('essential')

const userMenuVisible = ref(false)
const toast = reactive({
  visible: false,
  type: 'success' as 'success' | 'warning' | 'error',
  message: '',
  timer: undefined as ReturnType<typeof setTimeout> | undefined,
})
const showCareOrders = computed(
  () =>
    currentPage.value === 'home' &&
    (activePanel.panel === null ||
      activePanel.panel === 'profile' ||
      activePanel.panel === 'password'),
)
const isFullLayoutPage = computed(
  () =>
    currentPage.value === 'library' ||
    currentPage.value === 'users' ||
    currentPage.value === 'technicians' ||
    currentPage.value === 'schedule' ||
    currentPage.value === 'tasks-map' ||
    currentPage.value === 'coupons' ||
    currentPage.value === 'income-log' ||
    currentPage.value === 'settings' ||
    activePanel.panel === 'technicians',
)

/** HM Care Orders、Pending Schedule/Reschedule、FU Pending Schedule/Reschedule 显示订单列表 */
const showHMCareOrdersList = computed(
  () =>
    showCareOrders.value &&
    (isCareOrdersOverlayListRoute(route) ||
      careActiveSub.value === 'hm_orders' ||
      careActiveSub.value === 'pend_sched' ||
      careActiveSub.value === 'pend_resched' ||
      careActiveSub.value === 'fu_pend_sched' ||
      careActiveSub.value === 'fu_pend_resched'),
)

const showHMCareSubscriptions = computed(
  () => showCareOrders.value && careActiveSub.value === 'hm_care_subscription',
)

const careOrders = ref<OrderRecord[]>([])
provide('careOrders', careOrders)

/** 仅随 care-order/sidebar-stats 更新；初始为 0，避免首屏闪 MOCK */
const hmCareOrdersBadgeCount = ref(0)
provide('hmCareOrdersBadgeCount', hmCareOrdersBadgeCount)

const careSidebarCounts = ref({
  pendingSchedule: 0,
  pendingReschedule: 0,
  fuPendingSchedule: 0,
  fuPendingReschedule: 0,
})
provide('careSidebarCounts', careSidebarCounts)

let careSidebarBadgeRefreshInFlight = false
let careSidebarBadgeRefreshQueued = false

/** 写入侧栏 HM 总数 + 四桶角标（页面刷新 / 子组件 inject 调用） */
async function applyCareSidebarBadgeRefresh(): Promise<CareSidebarBadgeRefreshResult | null> {
  if (careSidebarBadgeRefreshInFlight) {
    careSidebarBadgeRefreshQueued = true
    return null
  }
  careSidebarBadgeRefreshInFlight = true
  let result: CareSidebarBadgeRefreshResult | null = null
  try {
    const apiResult = await refreshCareSidebarBadges()
    hmCareOrdersBadgeCount.value = apiResult.hmCareOrdersTotal
    careSidebarCounts.value = apiResult.sidebarCounts
    result = apiResult
  } catch {
    result = null
  } finally {
    careSidebarBadgeRefreshInFlight = false
    if (careSidebarBadgeRefreshQueued) {
      careSidebarBadgeRefreshQueued = false
      const queued = await applyCareSidebarBadgeRefresh()
      if (queued) result = queued
    }
  }
  return result
}

provide('refreshCareSidebarBadges', applyCareSidebarBadgeRefresh)

function onCareSidebarBadgeVisibilityChange(): void {
  if (document.visibilityState === 'visible' && !showHMCareOrdersList.value) {
    void applyCareSidebarBadgeRefresh()
  }
}

async function fetchAdminProfile(): Promise<void> {
  try {
    const response = await getAdminProfile()
    if (response.code === 200) {
      const profile: AdminProfile = response.data
      sidebarName.value = profile.nickname || profile.username
      sidebarRole.value = profile.role === 'admin' ? 'Super Admin' : 'Admin'
      if (profile.avatar) {
        const avatarBaseUrl = import.meta.env.VITE_BASE_URL || ''
        sidebarAvatar.value = profile.avatar.startsWith('http')
          ? profile.avatar
          : `${avatarBaseUrl}${profile.avatar}`
      }
    }
  } catch (error) {
    console.error('Failed to fetch admin profile:', error)
  }
}

onMounted(() => {
  document.addEventListener('visibilitychange', onCareSidebarBadgeVisibilityChange)
  // 点击空白关闭用户菜单
  document.addEventListener('click', closeUserMenuByBlank)
  // 获取管理员资料
  void fetchAdminProfile()
  // 页面刷新 / 首次进入：拉 HM 总数 + 四桶角标
  void applyCareSidebarBadgeRefresh()
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onCareSidebarBadgeVisibilityChange)
  // 移除点击空白关闭用户菜单监听
  document.removeEventListener('click', closeUserMenuByBlank)
})

function closeUserMenuByBlank(e: MouseEvent) {
  const target = e.target as HTMLElement
  // 点击用户头像区域不关闭，其余空白关闭
  if (!target.closest('.user-info') && !target.closest('.user-menu')) {
    userMenuVisible.value = false
  }
}

function careBadgeText(n: number): string {
  return formatCareSidebarBadge(n)
}

function careSidebarBadgeVisible(n: number): boolean {
  return n > 0
}

function activateCareOrdersSub(key: CareSubKeyNav): void {
  libraryExpanded.value = false
  couponsExpanded.value = false
  careExpanded.value = true
  careActiveSub.value = key
  currentPage.value = 'home'
  activePanel.panel = null
}

// ✅ 修改1：点击HomeMetric Care时自动收起Library
function onCareHeaderClick(): void {
  libraryExpanded.value = false
  couponsExpanded.value = false
  if (careExpanded.value) {
    careExpanded.value = false
    return
  }
  activateCareOrdersSub('hm_orders')
  void router.push(careOrdersListRouteForSub('hm_orders'))
}

function selectCareSub(key: CareSubKeyNav): void {
  activateCareOrdersSub(key)
  void router.push(careOrdersListRouteForSub(key))
}

const CARE_ORDERS_LIST_SUBS: CareSubKeyNav[] = [
  'hm_orders',
  'pend_sched',
  'pend_resched',
  'fu_pend_sched',
  'fu_pend_resched',
]

function syncSideNavFromRoute(): void {
  if (isCareOrdersOverlayListRoute(route)) {
    careExpanded.value = true
    currentPage.value = 'home'
    const prefill = peekHmCareOrdersListPrefill()
    // 外链预填必须在主列表搜；其余：已在订单列表子项则保持，否则落到 hm_orders
    if (
      prefill?.careSubKey === 'hm_orders' ||
      prefill?.searchField === 'order_id' ||
      prefill?.searchField === 'home_id' ||
      prefill?.searchField === 'user_id'
    ) {
      careActiveSub.value = 'hm_orders'
    } else if (!CARE_ORDERS_LIST_SUBS.includes(careActiveSub.value)) {
      careActiveSub.value = 'hm_orders'
    }
    activePanel.panel = null
    libraryExpanded.value = false
    couponsExpanded.value = false
    return
  }

  const careSub = careSubKeyFromRoute(route)
  if (careSub) {
    careExpanded.value = true
    const prefill = peekHmCareOrdersListPrefill()
    if (
      prefill?.careSubKey === 'hm_orders' ||
      prefill?.searchField === 'order_id' ||
      prefill?.searchField === 'home_id' ||
      prefill?.searchField === 'user_id'
    ) {
      careActiveSub.value = 'hm_orders'
    } else {
      careActiveSub.value = careSub
    }
    currentPage.value = 'home'
    activePanel.panel = null
    libraryExpanded.value = false
    couponsExpanded.value = false
    return
  }

  const librarySub = librarySubKeyFromRoute(route)
  if (librarySub) {
    careExpanded.value = false
    couponsExpanded.value = false
    libraryExpanded.value = true
    libraryActiveSub.value = librarySub
    currentPage.value = 'library'
    activePanel.panel = null
    if (librarySub === 'templates') {
      careTemplateTab.value = careTemplateTabFromRoute(route)
    }
    return
  }

  if (route.name === 'CouponDetail' || route.name === 'CouponCopy') {
    careExpanded.value = false
    libraryExpanded.value = false
    couponsExpanded.value = true
    couponActiveSub.value = 'center'
    currentPage.value = 'coupons'
    activePanel.panel = null
    return
  }

  const couponSub = couponSubKeyFromRoute(route)
  if (couponSub) {
    careExpanded.value = false
    libraryExpanded.value = false
    couponsExpanded.value = true
    couponActiveSub.value = couponSub
    currentPage.value = 'coupons'
    activePanel.panel = null
    return
  }

  if (isUsersRoute(route)) {
    careExpanded.value = false
    libraryExpanded.value = false
    couponsExpanded.value = false
    currentPage.value = 'users'
    activePanel.panel = null
    return
  }

  if (route.name === 'Technicians') {
    careExpanded.value = false
    libraryExpanded.value = false
    couponsExpanded.value = false
    currentPage.value = 'technicians'
    activePanel.panel = 'technicians'
    return
  }

  if (route.name === 'Schedule') {
    careExpanded.value = false
    libraryExpanded.value = false
    couponsExpanded.value = false
    currentPage.value = 'schedule'
    activePanel.panel = null
    return
  }

  if (route.name === 'TasksMap') {
    careExpanded.value = false
    libraryExpanded.value = false
    couponsExpanded.value = false
    currentPage.value = 'tasks-map'
    activePanel.panel = null
    return
  }

  if (route.name === 'IncomeLog') {
    careExpanded.value = false
    libraryExpanded.value = false
    couponsExpanded.value = false
    currentPage.value = 'income-log'
    activePanel.panel = null
    return
  }

  if (route.name === 'Settings') {
    careExpanded.value = false
    libraryExpanded.value = false
    couponsExpanded.value = false
    currentPage.value = 'settings'
    activePanel.panel = null
  }
}

function handleLibraryClick(): void {
  if (libraryExpanded.value) {
    libraryExpanded.value = false
    return
  }
  careExpanded.value = false
  couponsExpanded.value = false
  activePanel.panel = null
  void router.push(libraryListRouteForSub('checklist'))
}

function selectLibrarySub(key: 'checklist' | 'templates' | 'default-checklist'): void {
  void router.push(libraryListRouteForSub(key))
}

function handleCareTemplateTabChange(tab: CareTemplateTab): void {
  careTemplateTab.value = tab
  if (route.name === 'LibraryCareTemplates') {
    void router.replace(careTemplatesRoute(tab))
  }
}

function handleProfileClick(): void {
  userMenuVisible.value = false
  careExpanded.value = false
  activePanel.panel = 'profile'
}

function handlePasswordClick(): void {
  userMenuVisible.value = false
  careExpanded.value = false
  activePanel.panel = 'password'
}

// ✅ 修改2：点击头像时自动收起Library
function toggleUserMenu(): void {
  userMenuVisible.value = !userMenuVisible.value
  libraryExpanded.value = false // 新增：收起Library下拉框
}

watch(
  () => currentPage.value,
  (page) => {
    if (page !== 'technicians') {
      showTechniciansPage.value = false
      return
    }
    showTechniciansPage.value = false
    void nextTick(() => {
      requestAnimationFrame(() => {
        showTechniciansPage.value = true
      })
    })
  },
  { flush: 'post' },
)

watch(
  () => route.fullPath,
  () => {
    syncSideNavFromRoute()
  },
  { immediate: true },
)

/** 旧链接 /home?panel=library|technicians 重定向到独立路由 */
watch(
  () => route.query.panel,
  (panel) => {
    if (panel !== 'technicians' && panel !== 'library') return
    if (panel === 'technicians') {
      void router.replace(techniciansRoute())
      return
    }
    const sub = route.query.sub === 'templates' ? 'templates' : 'checklist'
    void router.replace(libraryListRouteForSub(sub))
  },
  { immediate: true },
)
/** 新建订单页回跳：打开 HM Care Orders 主列表，便于消费 sessionStorage / 刷新列表 */
watch(
  () => route.query.careInsertLocal === '1' || route.query.careRefresh === '1',
  (focusHm) => {
    if (!focusHm) return
    if (careSubKeyFromRoute(route) === null) {
      void router.replace({ name: 'Home', query: route.query })
      return
    }
    careExpanded.value = true
    careActiveSub.value = 'hm_orders'
    currentPage.value = 'home'
    activePanel.panel = null
  },
  { immediate: true },
)

const hmCareOrdersRef = ref<{ openOrderDetailById: (orderId: string) => Promise<boolean> } | null>(
  null,
)

/** 旧链接 /home?orderDetail= 重定向到独立详情路由 */
watch(
  () => route.query.orderDetail,
  (raw) => {
    const orderId = typeof raw === 'string' ? raw.trim() : ''
    if (!orderId || route.name === 'HmCareOrdersOrderDetail') return
    void router.replace(careOrdersDetailRoute(orderId))
  },
  { immediate: true },
)

watch(
  () =>
    [
      route.name === 'HmCareOrdersOrderDetail'
        ? String(route.params.orderId ?? '').trim()
        : '',
      showHMCareOrdersList.value,
    ] as const,
  async ([orderId, listVisible]) => {
    if (!orderId || !listVisible) return
    for (let i = 0; i < 20; i++) {
      await nextTick()
      const opener = hmCareOrdersRef.value
      if (!opener) continue
      const opened = await opener.openOrderDetailById(orderId)
      if (!opened) {
        showToast('warning', 'Order not found.')
      }
      return
    }
    showToast('warning', 'Order not found.')
  },
  { immediate: true },
)

function showToast(type: 'success' | 'warning' | 'error', message: string): void {
  clearTimeout(toast.timer)
  if (toast.visible) {
    toast.type = type
    toast.message = message
    toast.visible = false
    nextTick(() => {
      toast.visible = true
    })
  } else {
    toast.type = type
    toast.message = message
    toast.visible = true
  }
  toast.timer = setTimeout(() => {
    toast.visible = false
  }, TOAST_DURATION.brief)
}

provide('showToast', showToast)

function handleProfileSaved(result: 'success' | 'error'): void {
  const messages = {
    success: 'Successfully saved',
    error: 'Please fill in all fields',
  }
  showToast(result === 'success' ? 'success' : 'error', messages[result])
}

function handleAvatarUpdate(newAvatarUrl: string): void {
  const avatarBaseUrl = import.meta.env.VITE_BASE_URL || ''
  // base64 预览图和 http(s) 完整 URL 直接用，只对相对路径才拼 base URL
  const fullAvatarUrl =
    newAvatarUrl.startsWith('http') || newAvatarUrl.startsWith('data:')
      ? newAvatarUrl
      : `${avatarBaseUrl}${newAvatarUrl}`
  sidebarAvatar.value = fullAvatarUrl
  // 同步头像到 localStorage（下次登录也能用）
  const saved = localStorage.getItem('userInfo')
  if (saved) {
    const userInfo = JSON.parse(saved) as Record<string, unknown>
    localStorage.setItem('userInfo', JSON.stringify({ ...userInfo, avatar: fullAvatarUrl }))
  }
}

function handleUsernameUpdate(newName: string): void {
  sidebarName.value = newName
  // 同步昵称到 localStorage（下次登录也能用）
  const saved = localStorage.getItem('userInfo')
  if (saved) {
    const userInfo = JSON.parse(saved) as Record<string, unknown>
    localStorage.setItem('userInfo', JSON.stringify({ ...userInfo, nickname: newName }))
  }
}

function handlePasswordSaved(result: 'success' | 'warning' | 'error'): void {
  const messages = {
    success: 'Successfully changed',
    warning: 'At least 8 characters, including 1 uppercase letter and 1 special character.',
    error: '',
  }
  showToast(result, messages[result])
}

function navigateToUsers(): void {
  careExpanded.value = false
  libraryExpanded.value = false
  couponsExpanded.value = false
  void router.push(usersRoute())
}

function navigateToTechnicians(): void {
  careExpanded.value = false
  libraryExpanded.value = false
  couponsExpanded.value = false
  void router.push(techniciansRoute())
}

function navigateToSchedule(): void {
  careExpanded.value = false
  libraryExpanded.value = false
  couponsExpanded.value = false
  activePanel.panel = null
  void router.push(scheduleRoute())
}

function navigateToTasksMap(): void {
  careExpanded.value = false
  libraryExpanded.value = false
  couponsExpanded.value = false
  activePanel.panel = null
  void router.push(tasksMapRoute())
}

function handleCouponsClick(): void {
  if (couponsExpanded.value) {
    couponsExpanded.value = false
    return
  }
  careExpanded.value = false
  libraryExpanded.value = false
  activePanel.panel = null
  void router.push(couponRouteForSub('center'))
}

function selectCouponSub(key: CouponSubKey): void {
  careExpanded.value = false
  libraryExpanded.value = false
  couponsExpanded.value = true
  couponActiveSub.value = key
  currentPage.value = 'coupons'
  activePanel.panel = null
  void router.push(couponRouteForSub(key))
}

function navigateToIncomeLog(): void {
  careExpanded.value = false
  libraryExpanded.value = false
  couponsExpanded.value = false
  void router.push(incomeLogRoute())
}

function navigateToSettings(): void {
  careExpanded.value = false
  libraryExpanded.value = false
  couponsExpanded.value = false
  void router.push(settingsRoute())
}

function handleLogout(): void {
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')
  router.push({ name: 'Login' })
}
</script>

<template>
  <div
    class="home-root"
    :class="{
      'home-root--care-page': showCareOrders,
      'home-root--hm-care-orders': showHMCareOrdersList,
      'home-root--full-layout': isFullLayoutPage,
      'home-root--tasks-map': currentPage === 'tasks-map',
      'home-root--schedule': currentPage === 'schedule',
      'home-root--gutter-scroll': currentPage !== 'schedule' && currentPage !== 'tasks-map',
      'home-root--income-log': currentPage === 'income-log',
      'home-root--users': currentPage === 'users',
      'home-root--technicians':
        currentPage === 'technicians' || activePanel.panel === 'technicians',
      'home-root--library': currentPage === 'library',
    }"
  >
    <aside class="sidebar">
      <div class="sidebar-header">
        <img class="sidebar-logo" :src="logoIcon" alt="HomeMetric Logo" />
      </div>

      <div class="sidebar-body">
        <nav class="sidebar-nav">
            <div class="nav-group">
              <div class="nav-item" :class="{ expanded: careExpanded }" @click="onCareHeaderClick">
                <img class="nav-icon" :src="homemetricIcon" alt="HomeMetric Care" />
                <span class="nav-root-title">HomeMetric Care</span>
                <img
                  class="dropdown-icon"
                  :src="careExpanded ? navArrowUpIcon : navArrowDownIcon"
                  alt=""
                  aria-hidden="true"
                />
              </div>
              <div v-show="careExpanded" class="nav-sublist">
                <button
                  type="button"
                  class="nav-sub"
                  :class="{ active: showCareOrders && careActiveSub === 'hm_orders' }"
                  @click.stop="selectCareSub('hm_orders')"
                >
                  <span
                    v-if="careSidebarBadgeVisible(hmCareOrdersBadgeCount)"
                    class="nav-badge nav-badge-blue"
                  >{{ careBadgeText(hmCareOrdersBadgeCount) }}</span>
                  <span v-else class="nav-badge-spacer" aria-hidden="true"></span>
                  <span class="nav-sub-label">HM Care Orders</span>
                </button>
                <button
                  type="button"
                  class="nav-sub"
                  :class="{ active: showCareOrders && careActiveSub === 'pend_sched' }"
                  @click.stop="selectCareSub('pend_sched')"
                >
                  <span
                    v-if="careSidebarBadgeVisible(careSidebarCounts.pendingSchedule)"
                    class="nav-badge nav-badge-red"
                  >{{ careBadgeText(careSidebarCounts.pendingSchedule) }}</span>
                  <span v-else class="nav-badge-spacer" aria-hidden="true"></span>
                  <span class="nav-sub-label">Pending Schedule</span>
                </button>
                <button
                  type="button"
                  class="nav-sub"
                  :class="{ active: showCareOrders && careActiveSub === 'pend_resched' }"
                  @click.stop="selectCareSub('pend_resched')"
                >
                  <span
                    v-if="careSidebarBadgeVisible(careSidebarCounts.pendingReschedule)"
                    class="nav-badge nav-badge-red"
                  >{{ careBadgeText(careSidebarCounts.pendingReschedule) }}</span>
                  <span v-else class="nav-badge-spacer" aria-hidden="true"></span>
                  <span class="nav-sub-label">Pending Reschedule</span>
                </button>
                <button
                  type="button"
                  class="nav-sub"
                  :class="{ active: showCareOrders && careActiveSub === 'fu_pend_sched' }"
                  @click.stop="selectCareSub('fu_pend_sched')"
                >
                  <span
                    v-if="careSidebarBadgeVisible(careSidebarCounts.fuPendingSchedule)"
                    class="nav-badge nav-badge-red"
                  >{{ careBadgeText(careSidebarCounts.fuPendingSchedule) }}</span>
                  <span v-else class="nav-badge-spacer" aria-hidden="true"></span>
                  <span class="nav-sub-label">FU Pending Schedule</span>
                </button>
                <button
                  type="button"
                  class="nav-sub"
                  :class="{ active: showCareOrders && careActiveSub === 'fu_pend_resched' }"
                  @click.stop="selectCareSub('fu_pend_resched')"
                >
                  <span
                    v-if="careSidebarBadgeVisible(careSidebarCounts.fuPendingReschedule)"
                    class="nav-badge nav-badge-red"
                  >{{ careBadgeText(careSidebarCounts.fuPendingReschedule) }}</span>
                  <span v-else class="nav-badge-spacer" aria-hidden="true"></span>
                  <span class="nav-sub-label">FU Pending Reschedule</span>
                </button>
                <button
                  type="button"
                  class="nav-sub"
                  :class="{ active: showCareOrders && careActiveSub === 'hm_care_subscription' }"
                  @click.stop="selectCareSub('hm_care_subscription')"
                >
                  <span class="nav-badge-spacer" aria-hidden="true"></span>
                  <span class="nav-sub-label">HM Care Subscription</span>
                </button>
              </div>
            </div>

            <div class="nav-group">
              <div
                class="nav-item"
                :class="{ expanded: libraryExpanded }"
                @click="handleLibraryClick"
              >
                <img class="nav-icon" :src="categoryIcon" alt="Library" />
                <span class="nav-root-title">Library</span>
                <img
                  class="dropdown-icon"
                  :src="libraryExpanded ? navArrowUpIcon : navArrowDownIcon"
                  alt=""
                  aria-hidden="true"
                />
              </div>
              <div v-show="libraryExpanded" class="nav-sublist">
                <button
                  type="button"
                  class="nav-sub"
                  :class="{ active: route.name === 'LibraryChecklistItems' }"
                  @click.stop="selectLibrarySub('checklist')"
                >
                  <span class="nav-badge-spacer" aria-hidden="true"></span>
                  <span class="nav-sub-label">Checklist Items</span>
                </button>
                <button
                  type="button"
                  class="nav-sub"
                  :class="{ active: route.name === 'LibraryCareTemplates' }"
                  @click.stop="selectLibrarySub('templates')"
                >
                  <span class="nav-badge-spacer" aria-hidden="true"></span>
                  <span class="nav-sub-label">Care Templates</span>
                </button>
                <button
                  type="button"
                  class="nav-sub"
                  :class="{ active: route.name === 'LibraryDefaultChecklist' }"
                  @click.stop="selectLibrarySub('default-checklist')"
                >
                  <span class="nav-badge-spacer" aria-hidden="true"></span>
                  <span class="nav-sub-label">Default Checklist</span>
                </button>
              </div>
            </div>

            <div
              class="nav-item"
              :class="{ active: currentPage === 'users' || isUsersRoute(route) }"
              @click="navigateToUsers"
            >
              <img class="nav-icon" :src="usersIcon" alt="Users" />
              <span class="nav-root-title">Users</span>
            </div>

            <div
              class="nav-item"
              :class="{ active: currentPage === 'technicians' }"
              @click="navigateToTechnicians"
            >
              <img class="nav-icon" :src="technicianIcon" alt="Technicians" />
              <span class="nav-root-title">Technicians</span>
            </div>

            <div
              class="nav-item"
              :class="{ active: currentPage === 'schedule' }"
              @click="navigateToSchedule"
            >
              <img class="nav-icon" :src="scheduleIcon" alt="Schedule" />
              <span class="nav-root-title">Schedule</span>
            </div>

            <div
              class="nav-item"
              :class="{ active: currentPage === 'tasks-map' }"
              @click="navigateToTasksMap"
            >
              <img class="nav-icon" :src="mapsIcon" alt="Tasks and Map" />
              <span class="nav-root-title">Tasks and Map</span>
            </div>

            <div class="nav-group">
              <div
                class="nav-item"
                :class="{ expanded: couponsExpanded }"
                @click="handleCouponsClick"
              >
                <img class="nav-icon" :src="couponIcon" alt="Coupons" />
                <span class="nav-root-title">Coupons</span>
                <img
                  class="dropdown-icon"
                  :src="couponsExpanded ? navArrowUpIcon : navArrowDownIcon"
                  alt=""
                  aria-hidden="true"
                />
              </div>
              <div v-show="couponsExpanded" class="nav-sublist">
                <button
                  type="button"
                  class="nav-sub"
                  :class="{ active: route.name === 'CouponCenter' || route.name === 'CouponDetail' || route.name === 'CouponCopy' }"
                  @click.stop="selectCouponSub('center')"
                >
                  <span class="nav-badge-spacer" aria-hidden="true"></span>
                  <span class="nav-sub-label">Coupon Center</span>
                </button>
                <button
                  type="button"
                  class="nav-sub"
                  :class="{ active: route.name === 'CouponUsageLog' }"
                  @click.stop="selectCouponSub('usage-log')"
                >
                  <span class="nav-badge-spacer" aria-hidden="true"></span>
                  <span class="nav-sub-label">Coupon Usage Log</span>
                </button>
              </div>
            </div>

            <div
              class="nav-item"
              :class="{ active: currentPage === 'income-log' }"
              @click="navigateToIncomeLog"
            >
              <img class="nav-icon" :src="incomeIcon" alt="Income Log" />
              <span class="nav-root-title">Income Log</span>
            </div>

            <div
              class="nav-item"
              :class="{ active: currentPage === 'settings' }"
              @click="navigateToSettings"
            >
              <svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.2 7.2 0 0 0-1.63-.94l-.36-2.54a.5.5 0 0 0-.5-.42h-3.84a.5.5 0 0 0-.5.42l-.36 2.54c-.59.22-1.13.54-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.71 8.84a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32c.13.22.39.31.6.22l2.39-.96c.5.4 1.04.72 1.63.94l.36 2.54c.05.24.25.42.5.42h3.84c.25 0 .45-.18.5-.42l.36-2.54c.59-.22 1.13-.54 1.63-.94l2.39.96c.22.09.47 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z"
                />
              </svg>
              <span class="nav-root-title">Settings</span>
            </div>
          </nav>
      </div>

      <div class="sidebar-footer">
          <div class="user-info" @click="toggleUserMenu">
            <img
          v-if="sidebarAvatar"
          class="user-avatar"
          :src="sidebarAvatar"
          alt="avatar"
          draggable="false"
          @error="(e) => (e.target as HTMLImageElement).src = ''"
        />
        <div v-else class="user-avatar-placeholder">
          <span class="avatar-initial">{{ sidebarName.charAt(0) }}</span>
        </div>
            <div class="user-details">
              <div class="user-name">{{ sidebarName }}</div>
              <div class="user-role">Super Admin</div>
            </div>
          </div>
          <div v-show="userMenuVisible" class="user-menu" @click.stop>
            <div class="menu-items">
              <button class="menu-item" @click="handleProfileClick">
                <span>Profile</span>
              </button>
              <button class="menu-item" @click="handlePasswordClick">
                <span>Change Password</span>
              </button>
              <div class="menu-divider" aria-hidden="true"></div>
              <button class="menu-item logout" @click="handleLogout">
                <img class="logout-icon" :src="logoutIcon" alt="logout" />
                <span>Log out</span>
              </button>
            </div>
          </div>
        </div>
    </aside>

    <main
      class="main-content"
      :class="{
        'care-layout': showCareOrders || showHMCareSubscriptions,
        'care-orders-window-scroll': showHMCareOrdersList || showHMCareSubscriptions,
        'full-layout': isFullLayoutPage || showHMCareSubscriptions,
        'gutter-scroll-layout': currentPage !== 'schedule' && currentPage !== 'tasks-map',
        'income-log-layout': currentPage === 'coupons',
        'income-log-page-layout': currentPage === 'income-log',
        'users-page-layout': currentPage === 'users',
        'schedule-layout': currentPage === 'schedule',
        'tasks-map-layout': currentPage === 'tasks-map',
      }"
    >
      <div
        class="content-wrapper"
        :class="{
          'care-fill': showCareOrders || showHMCareSubscriptions,
          'full-fill':
            isFullLayoutPage ||
            showHMCareSubscriptions ||
            currentPage === 'users' ||
            currentPage === 'technicians' ||
            currentPage === 'schedule' ||
            currentPage === 'tasks-map' ||
            currentPage === 'coupons' ||
            currentPage === 'income-log' ||
            currentPage === 'settings',
        }"
      >
        <HMCareOrders
          v-if="showHMCareOrdersList"
          ref="hmCareOrdersRef"
          :care-sub-key="careActiveSub"
          @toast="({ type, message }) => showToast(type, message)"
        />

        <HMCareSubscriptionsPage v-if="showHMCareSubscriptions" />

        <ChecklistItems v-if="currentPage === 'library' && libraryActiveSub === 'checklist'" />

        <div v-if="currentPage === 'library' && libraryActiveSub === 'templates'">
          <CareTemplates @tab-change="handleCareTemplateTabChange" />
          <EssentialCareTemplates v-if="careTemplateTab === 'essential'" />
          <EnhancedCareTemplates v-if="careTemplateTab === 'enhanced'" />
          <PremiumCare v-if="careTemplateTab === 'premium'" :active-tab="careTemplateTab" />
        </div>

        <DefaultChecklist
          v-if="currentPage === 'library' && libraryActiveSub === 'default-checklist'"
          :key="'default-checklist-list'"
        />
        <UsersPage v-if="currentPage === 'users'" />
        <TechniciansPage v-if="showTechniciansPage" :show-toast="showToast" />
        <SchedulePage v-if="currentPage === 'schedule'" />
        <TMPage v-if="currentPage === 'tasks-map'" />
        <CouponCenter v-if="currentPage === 'coupons' && couponActiveSub === 'center'" />
        <CouponUsageLog v-if="currentPage === 'coupons' && couponActiveSub === 'usage-log'" />
        <IncomeLogPage v-if="currentPage === 'income-log'" />
        <SettingsPage v-if="currentPage === 'settings'" />
      </div>

      <ProfilePage
        v-if="activePanel.panel === 'profile'"
        :current-avatar="sidebarAvatar"
        :current-username="sidebarName"
        @close="activePanel.panel = null"
        @saved="handleProfileSaved"
        @update-avatar="handleAvatarUpdate"
        @update-username="handleUsernameUpdate"
      />

      <ChangePassword
        v-if="activePanel.panel === 'password'"
        @close="activePanel.panel = null"
        @saved="handlePasswordSaved"
      />
    </main>

    <Teleport to="body">
      <ToastComponent :visible="toast.visible" :type="toast.type" :message="toast.message" />
    </Teleport>
  </div>
</template>
<style scoped>
/* 根容器不用 flex：侧栏为 fixed，避免与主区 flex 挤压；整页宽度见 main.css 中 #app 拉伸 */
.home-root {
  display: block;
  min-height: 100vh;
  overflow: hidden;
  background: #f5f6fb;
  width: 100%;
  --nav-selected-w: 220px;
  --nav-selected-h: 40px;
  --nav-selected-inset-x: 8px;
  --hm-care-sidebar-w: calc(var(--nav-selected-w) + var(--nav-selected-inset-x) * 2);
  font-family:
    system-ui,
    -apple-system,
    'Segoe UI',
    sans-serif;
}

.home-root--care-page {
  overflow-x: hidden;
  overflow-y: visible;
  background: #f5f6fb;
  width: 100%;
}

.home-root--hm-care-orders {
  overflow: visible;
  height: auto;
  min-height: 100vh;
}

.home-root--tasks-map {
  background: #1d1d1d;
  overflow: hidden;
  height: 100vh;
  width: 100%;
}

.home-root--schedule {
  background: #f5f6fb;
  overflow-x: hidden;
}

.home-root--gutter-scroll {
  box-sizing: border-box;
  width: 100%;
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: #d0d0d0 transparent;
  /* 12px 留白 + gutter ≈ 12px = 总右侧 24px */
  padding-right: 12px;
}

.home-root--gutter-scroll.home-root--users,
.home-root--gutter-scroll.home-root--technicians,
.home-root--gutter-scroll.home-root--library {
  /* 抵消页内负 margin 后，白卡到右缘仍约 24px（含 gutter） */
  padding-right: 24px;
}

.home-root--gutter-scroll::-webkit-scrollbar {
  width: 12px;
}

.home-root--gutter-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.home-root--gutter-scroll::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 6px;
}

.sidebar {
  width: var(--hm-care-sidebar-w);
  min-width: var(--hm-care-sidebar-w);
  max-width: var(--hm-care-sidebar-w);
  flex-shrink: 0;
  background: #000;
  color: #fff;
  display: flex;
  flex-direction: column;
  padding: 0;
  position: fixed;
  height: 100vh;
  min-height: 100vh;
  left: 0;
  top: 0;
  overflow: hidden;
  z-index: 10;
  box-sizing: border-box;
}

.sidebar-body {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.35) transparent;
}

.sidebar-body::-webkit-scrollbar {
  width: 6px;
}

.sidebar-body::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.35);
  border-radius: 3px;
}

.sidebar-body::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

.sidebar-header {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
  padding: 24px 76px 32px 56px;
  margin-bottom: -8px;
}

.sidebar-logo {
  width: 88px;
  height: 88px;
  margin: 0;
  object-fit: contain;
}

.sidebar-nav {
  --nav-selected-bg: #484848;
  --nav-root-gap: 16px;
  padding: 0 0 0 0;
  display: flex;
  flex-direction: column;
  gap: var(--nav-root-gap);
  width: 100%;
  box-sizing: border-box;
}

.nav-group {
  margin-bottom: 0;
  width: 100%;
  box-sizing: border-box;
}

.nav-sublist {
  --nav-sub-badge-gap: 8px;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--nav-sub-badge-gap);
}
.nav-group:has(.nav-sublist) > .nav-item {
  padding-bottom: 0;
}

.nav-sub {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  width: 100%;
  height: var(--nav-selected-h);
  min-height: var(--nav-selected-h);
  max-height: var(--nav-selected-h);
  padding: 0 16px 0 calc(var(--nav-selected-inset-x) + 16px);
  box-sizing: border-box;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 0.88rem;
  cursor: pointer;
  text-align: left;
  overflow: hidden;
  flex-shrink: 0;
}
.nav-sub::before {
  content: '';
  position: absolute;
  left: var(--nav-selected-inset-x);
  top: 0;
  width: var(--nav-selected-w);
  height: var(--nav-selected-h);
  border-radius: 8px;
  background: transparent;
  z-index: 0;
  pointer-events: none;
}

.nav-sub.active::before {
  background: var(--nav-selected-bg);
}

.nav-sub .nav-badge,
.nav-sub .nav-badge-spacer,
.nav-sub .nav-sub-label {
  position: relative;
  z-index: 1;
}

.nav-sub-label {
  flex: 0 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0;
  color: #ffffff;
  text-align: left;
  margin: 0;
  padding: 0;
  white-space: nowrap;
}

.nav-sub.active .nav-sub-label {
  font-size: 14px;
  font-weight: 400;
}

.nav-badge {
  --nav-sub-badge-r: 11px;
  width: calc(var(--nav-sub-badge-r) * 2);
  height: calc(var(--nav-sub-badge-r) * 2);
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  color: #fff;
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: var(--nav-sub-badge-r);
}

.nav-badge-spacer {
  --nav-sub-badge-r: 11px;
  width: calc(var(--nav-sub-badge-r) * 2);
  height: calc(var(--nav-sub-badge-r) * 2);
  flex-shrink: 0;
  box-sizing: border-box;
  border-radius: var(--nav-sub-badge-r);
}

.nav-badge-blue {
  background: #00aeef;
}

.nav-badge-red {
  background: #e54848;
}

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
  height: var(--nav-selected-h);
  min-height: var(--nav-selected-h);
  max-height: var(--nav-selected-h);
  box-sizing: border-box;
  padding: 0 30px 0 calc(var(--nav-selected-inset-x) + 16px);
  border-radius: 0;
  cursor: pointer;
  transition: background 0.2s ease;
  font-size: 0.95rem;
  color: #fff;
  background: transparent;
}

.nav-item::before {
  content: '';
  position: absolute;
  left: var(--nav-selected-inset-x);
  top: 0;
  width: var(--nav-selected-w);
  height: var(--nav-selected-h);
  border-radius: 8px;
  background: transparent;
  z-index: 0;
  pointer-events: none;
  transition: background 0.2s ease;
}

.nav-item.active::before {
  background: var(--nav-selected-bg);
}

.nav-item img.nav-icon,
.nav-item svg.nav-icon {
  position: relative;
  z-index: 1;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  margin-right: 8px;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

.nav-root-title:has(+ .dropdown-icon) {
  position: relative;
  z-index: 1;
  margin-right: 4px;
}

.nav-item .dropdown-icon {
  position: relative;
  z-index: 1;
  width: 16px;
  height: 16px;
  margin-left: 0;
  flex-shrink: 0;
  object-fit: contain;
  display: block;
}

.nav-root-title {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  width: auto;
  max-width: none;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0;
  color: #ffffff;
  text-align: left;
  margin: 0;
  padding: 0;
  white-space: nowrap;
}
.sidebar-footer {
  --user-menu-w: 188px;
  --user-menu-h: 137px;
  --user-menu-side: 16px;
  --user-menu-x: calc(var(--nav-selected-inset-x) + var(--user-menu-side));
  flex-shrink: 0;
  padding: 0 0 24px;
  position: relative;
  box-sizing: border-box;
}

.user-menu {
  position: absolute;
  bottom: calc(100% + 8px);
  left: var(--user-menu-x);
  width: var(--user-menu-w);
  height: var(--user-menu-h);
  background: #fff;
  border-radius: 12px;
  color: #333;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  z-index: 100;
  pointer-events: auto;
}

.menu-items {
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  padding-bottom: 8px;
}
.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 var(--user-menu-side) 0 var(--user-menu-x);
  min-width: 121px;
  height: 40px;
  cursor: pointer;
  border-radius: 8px;
  margin-top: 12px;
  box-sizing: border-box;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f0f0f0;
  object-fit: cover;
  flex-shrink: 0;
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-weight: 600;
  font-size: 14px;
  color: #fff;
  line-height: 20px;
  white-space: nowrap;
}

.user-role {
  font-size: 12px;
  font-weight: 400;
  color: #999;
  line-height: 16px;
  white-space: nowrap;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 32px;
  padding: 0 var(--user-menu-side);
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  color: #000000;
  box-sizing: border-box;
}
.menu-item:first-child {
  margin-top: 8px;
}

.menu-item:nth-child(2) {
  margin-top: 8px;
  margin-bottom: 8px;
}

.menu-divider {
  width: calc(100% - var(--user-menu-side) * 2);
  height: 1px;
  margin: 0 var(--user-menu-side);
  background: #d8d8d8;
  flex-shrink: 0;
}

.menu-item.logout {
  margin-top: 8px;
  height: 32px;
  box-sizing: border-box;
}

.logout-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.main-content {
  margin-left: var(--hm-care-sidebar-w);
  padding: 32px;
  position: relative;
  height: 100vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.main-content.care-layout {
  padding: 0;
  width: calc(100vw - var(--hm-care-sidebar-w));
  max-width: calc(100vw - var(--hm-care-sidebar-w));
  height: auto;
  min-height: 100vh;
  overflow: visible;
  display: block;
}

.main-content.care-orders-window-scroll {
  width: calc(100vw - var(--hm-care-sidebar-w));
  max-width: calc(100vw - var(--hm-care-sidebar-w));
  height: auto;
  min-height: 100vh;
  max-height: none;
  overflow: visible;
}

.main-content.full-layout {
  width: calc(100vw - var(--hm-care-sidebar-w));
  max-width: calc(100vw - var(--hm-care-sidebar-w));
  height: auto;
  min-height: 100vh;
  overflow: visible;
  display: block;
}

.main-content.full-layout.income-log-layout {
  /* Coupons：左/上/下 24px；右侧交给 home-root gutter */
  padding: 24px 0 24px 24px;
  box-sizing: border-box;
  width: auto;
  max-width: none;
}

.main-content.full-layout.income-log-page-layout {
  /* Income Log：左/上/下 24px；右侧交给 home-root gutter */
  box-sizing: border-box;
  width: auto;
  max-width: none;
  height: auto;
  min-height: 100%;
  overflow: visible;
  padding: 24px 0 24px 24px;
}

.main-content.gutter-scroll-layout {
  box-sizing: border-box;
  width: auto;
  max-width: none;
  height: auto;
  min-height: 100%;
  overflow: visible;
  padding-right: 0;
}

.main-content.full-layout.users-page-layout {
  /* 不改 Users 内部布局；右侧由 gutter-scroll-layout + home-root 处理 */
  box-sizing: border-box;
  width: auto;
  max-width: none;
  height: auto;
  min-height: 100%;
  overflow: visible;
}

.main-content.full-layout.schedule-layout {
  padding: 0 !important;
  width: calc(100vw - var(--hm-care-sidebar-w));
  max-width: none;
  box-sizing: border-box;
}

.main-content.schedule-layout {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.main-content.full-layout.schedule-layout .content-wrapper {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
}

.main-content.full-layout.tasks-map-layout {
  --nav-selected-w: 220px;
  --nav-selected-inset-x: 8px;
  --hm-care-sidebar-w: calc(var(--nav-selected-w) + var(--nav-selected-inset-x) * 2);
  position: fixed;
  top: 0;
  bottom: 0;
  left: var(--hm-care-sidebar-w);
  right: 0;
  width: calc(100vw - var(--hm-care-sidebar-w));
  margin: 0 !important;
  padding: 0 !important;
  max-width: none !important;
  height: auto;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 1;
}

.main-content.full-layout.tasks-map-layout .content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0 !important;
  margin: 0 !important;
  width: 100% !important;
  max-width: none !important;
}

.content-wrapper {
  margin: 0 auto;
  width: 100%;
  padding-bottom: 32px;
}

.content-wrapper.care-fill {
  max-width: none;
  margin: 0;
  padding: 0;
  flex: 1 0 auto;
  min-height: min-content;
  display: block;
  overflow: visible;
}

.content-wrapper.full-fill {
  max-width: none;
  margin: 0;
  padding: 0;
  width: 100%;
}
.welcome-content h1 {
  font-size: 2rem;
  color: #1a1a1a;
  margin-bottom: 16px;
}

.welcome-content p {
  font-size: 1.1rem;
  color: #666;
}

@media (max-width: 768px) {
  .main-content:not(.tasks-map-layout):not(.schedule-layout) {
    padding: 20px;
  }

  .sidebar-header {
    padding-left: 24px;
    padding-right: 24px;
  }

  .sidebar-logo {
    width: 72px;
    height: 72px;
  }
}

@media (max-width: 480px) {
  .main-content:not(.tasks-map-layout):not(.schedule-layout) {
    padding: 16px;
  }
}
</style>
